Px.Editor.OrganizePages = class OrganizePages extends Px.Editor.BaseComponent {

  template() {
    return Px.template`
      <div class="px-organize-pages" data-can-add-or-remove-pages="${this.canAddOrRemovePages}">
        <div class="px-organize-pages-header">
          <div class="px-title">
            <h2>${Px.t('Organize pages')}</h2>
            <p>
              ${Px.t('Add, rearrange, or delete pages.')}
              ${Px.if(this.pageIncrements && this.setsPerIncrement !== 1, () => {
                return this.pageIncrementsInfo;
              })}
            </p>
          </div>
        </div>
        <div class="px-page-sets">
          ${this.projectStore.editor_sets_with_definitions.map(set_with_definition => {
            const set = set_with_definition.set;
            const definition = set_with_definition.definition;
            return Px.template`
              ${this.renderChild(Px.Editor.OrganizePages.PageSet, `set-${set.id}`, this.pageSetProps(set, definition))}
              ${Px.if(this.displayAddPagesButton(set, definition), () => {
                return Px.template`
                  <div class="px-page-set px-add-set" data-onclick="addPages">
                    <div class="px-page-thumbs">
                      <span>+</span>
                    </div>
                    <div class="px-page-captions">
                      <div class="px-page-caption">
                        ${Px.t('Add Page')}
                      </div>
                    </div>
                  </div>
                `;
              })}
            `;
          })}
        </div>
        <div class="px-organize-pages-sidebar">
          <div class="px-actions">
            <button class="px-duplicate-pages"
                    data-px-tooltip="${this.duplicateButtonTitle}"
                    data-onclick="duplicatePageSets"
                    ${this.duplicateButtonEnabled ? '' : 'disabled'}>
              ${Px.t('Duplicate page')}
              <span class="px-bubble">
                ${this.selectedPageCount}
                ${Px.raw(OrganizePages.icons.duplicate_pages)}
              </span>
            </button>
            <button class="px-delete-pages"
                    data-px-tooltip="${this.deleteButtonTitle}"
                    data-onclick="deletePageSets"
                    ${this.deleteButtonEnabled ? '' : 'disabled'}>
              ${Px.t('Delete page')}
              <span class="px-bubble">
                ${this.selectedPageCount}
                ${Px.raw(OrganizePages.icons.delete_pages)}
              </span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  constructor(props) {
    super(props);
    this.selectOrDeselectSet = this.selectOrDeselectSet.bind(this);
  }

  get dataProperties() {
    return {
      store: {required: true}
    };
  }

  static get properties() {
    return {
      selected_sets: {type: 'array', std: mobx.observable.array()}
    };
  }

  static get computedProperties() {
    return {
      projectStore: function() {
        return this.data.store.project;
      },
      themeStore: function() {
        return this.data.store.theme;
      },
      pageIncrements: function() {
        return this.themeStore.page_increments;
      },
      setsPerIncrement: function() {
        const grow_set_definition = this.themeStore.grow_set_definition;
        if (grow_set_definition) {
          return this.themeStore.page_increments / grow_set_definition.pages.length;
        } else {
          return null;
        }
      },
      pageIncrementsInfo: function() {
        const template = Px.t('Pages can be added or removed {{count}} at a time.');
        return template.replace('{{count}}', this.pageIncrements);
      },
      deleteButtonEnabled: function() {
        return this.projectStore.can_delete_pages && this.state.selected_sets.length === this.setsPerIncrement;
      },
      duplicateButtonEnabled: function() {
        return this.projectStore.can_add_pages && this.state.selected_sets.length === this.setsPerIncrement;
      },
      deleteButtonTitle: function() {
        if (this.deleteButtonEnabled) {
          return Px.t('Click to delete selected pages.');
        } else if (!this.projectStore.can_delete_pages) {
          return Px.t('Cannot remove any more pages from this project.');
        } else if (this.state.selected_sets.length === 0) {
          return Px.t('Select {{count}} pages to delete').replace('{{count}}', this.pageIncrements);
        } else {
          return Px.t('Can only delete pages {{count}} at a time.').replace('{{count}}', this.pageIncrements);
        }
      },
      duplicateButtonTitle: function() {
        if (this.duplicateButtonEnabled) {
          return Px.t('Click to duplicate selected pages.');
        } else if (!this.projectStore.can_add_pages) {
          return Px.t('Cannot add any more pages to this project.');
        } else if (this.state.selected_sets.length === 0) {
          return Px.t('Select {{count}} pages to duplicate').replace('{{count}}', this.pageIncrements);
        } else {
          return Px.t('Can only duplicate pages {{count}} at a time.').replace('{{count}}', this.pageIncrements);
        }
      },
      selectedPageCount: function() {
        let count = 0;
        this.state.selected_sets.forEach(set => {
          count += set.pages.length;
        });
        return count;
      },
      canAddOrRemovePages: function() {
        const theme_store = this.data.store.theme;
        return theme_store.page_increments && (theme_store.max_pages !== theme_store.min_pages);
      }
    };
  }

  isSelected(set) {
    return _.include(this.state.selected_sets, set);
  }

  selectOrDeselectSet(set) {
    const selected_sets = this.state.selected_sets;
    mobx.runInAction(() => {
      if (this.isSelected(set)) {
        selected_sets.remove(set);
      } else {
        selected_sets.push(set);
        if (selected_sets.length > this.setsPerIncrement) {
          selected_sets.shift();
        }
      }
    });
  }

  pageSetProps(set, definition) {
    return {
      set: set,
      set_definition: definition,
      selected: this.isSelected(set),
      thumbnail_size: 96,
      selectOrDeselectSet: this.selectOrDeselectSet
    };
  }

  // -------
  // Private
  // -------

  displayAddPagesButton(set, definition) {
    const project_store = this.data.store.project;
    const theme_store = this.data.store.theme;
    if (!project_store.can_add_pages) {
      return false;
    }
    // If this is the last growable set, we can put the button after it.
    if (set === project_store.last_growable_set) {
      return true;
    }
    // The only other valid option where we can display the add pages button is
    // when non-growable sets are completely filled, and the next page would belong to the grow set.
    if (!project_store.last_growable_set &&
        set.pages.length === definition.pages.length &&
        set === project_store.last_fixed_start_set &&
        theme_store.grow_set_definition) {
      return true;
    }
    return false;
  }

  // --------------
  // Event handlers
  // --------------

  addPages(evt) {
    this.data.store.addPages();
  }

  duplicatePageSets(evt) {
    mobx.runInAction(() => {
      this.data.store.duplicateSets(this.state.selected_sets);
      this.state.selected_sets.clear();
    });
  }

  deletePageSets(evt) {
    mobx.runInAction(() => {
      this.data.store.deleteSets(this.state.selected_sets);
      this.state.selected_sets.clear();
    });
  }

};

Px.Editor.OrganizePages.icons = {
  duplicate_pages: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5.00004 13.3335H4.16671C3.94569 13.3335 3.73373 13.2457 3.57745 13.0894C3.42117 12.9331 3.33337 12.7212 3.33337 12.5002V4.16683C3.33337 3.94582 3.42117 3.73385 3.57745 3.57757C3.73373 3.42129 3.94569 3.3335 4.16671 3.3335H12.5C12.7211 3.3335 12.933 3.42129 13.0893 3.57757C13.2456 3.73385 13.3334 3.94582 13.3334 4.16683V5.00016M7.50004 16.6668H15.8334C16.0544 16.6668 16.2664 16.579 16.4226 16.4228C16.5789 16.2665 16.6667 16.0545 16.6667 15.8335V7.50016C16.6667 7.27915 16.5789 7.06719 16.4226 6.91091C16.2664 6.75463 16.0544 6.66683 15.8334 6.66683H7.50004C7.27903 6.66683 7.06707 6.75463 6.91079 6.91091C6.75451 7.06719 6.66671 7.27915 6.66671 7.50016V15.8335C6.66671 16.0545 6.75451 16.2665 6.91079 16.4228C7.06707 16.579 7.27903 16.6668 7.50004 16.6668Z" stroke="currentColor" stroke-width="1.16667" stroke-linejoin="round"/></svg>',
  delete_pages: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11.9 1.6665C12.2498 1.66659 12.5907 1.77674 12.8744 1.98134C13.1581 2.18595 13.3703 2.47463 13.4808 2.8065L13.9333 4.1665H16.6667C16.8877 4.1665 17.0996 4.2543 17.2559 4.41058C17.4122 4.56686 17.5 4.77882 17.5 4.99984C17.5 5.22085 17.4122 5.43281 17.2559 5.58909C17.0996 5.74537 16.8877 5.83317 16.6667 5.83317L16.6642 5.89234L15.9417 16.0115C15.8966 16.642 15.6143 17.232 15.1517 17.6628C14.6891 18.0936 14.0805 18.3331 13.4483 18.3332H6.55167C5.91955 18.3331 5.31092 18.0936 4.84831 17.6628C4.38569 17.232 4.10342 16.642 4.05833 16.0115L3.33583 5.8915C3.33433 5.87209 3.33349 5.85264 3.33333 5.83317C3.11232 5.83317 2.90036 5.74537 2.74408 5.58909C2.5878 5.43281 2.5 5.22085 2.5 4.99984C2.5 4.77882 2.5878 4.56686 2.74408 4.41058C2.90036 4.2543 3.11232 4.1665 3.33333 4.1665H6.06667L6.51917 2.8065C6.62975 2.47449 6.84203 2.18571 7.12592 1.9811C7.4098 1.77649 7.75089 1.66642 8.10083 1.6665H11.9ZM14.9975 5.83317H5.0025L5.72083 15.8923C5.73579 16.1025 5.82981 16.2992 5.98397 16.4429C6.13812 16.5865 6.34096 16.6664 6.55167 16.6665H13.4483C13.659 16.6664 13.8619 16.5865 14.016 16.4429C14.1702 16.2992 14.2642 16.1025 14.2792 15.8923L14.9975 5.83317ZM8.33333 8.33317C8.53744 8.3332 8.73445 8.40813 8.88698 8.54377C9.03951 8.6794 9.13695 8.86629 9.16083 9.069L9.16667 9.1665V13.3332C9.16643 13.5456 9.0851 13.7499 8.93929 13.9043C8.79349 14.0588 8.59421 14.1517 8.38217 14.1641C8.17014 14.1766 7.96135 14.1076 7.79847 13.9713C7.6356 13.835 7.53092 13.6416 7.50583 13.4307L7.5 13.3332V9.1665C7.5 8.94549 7.5878 8.73353 7.74408 8.57725C7.90036 8.42097 8.11232 8.33317 8.33333 8.33317ZM11.6667 8.33317C11.8877 8.33317 12.0996 8.42097 12.2559 8.57725C12.4122 8.73353 12.5 8.94549 12.5 9.1665V13.3332C12.5 13.5542 12.4122 13.7661 12.2559 13.9224C12.0996 14.0787 11.8877 14.1665 11.6667 14.1665C11.4457 14.1665 11.2337 14.0787 11.0774 13.9224C10.9211 13.7661 10.8333 13.5542 10.8333 13.3332V9.1665C10.8333 8.94549 10.9211 8.73353 11.0774 8.57725C11.2337 8.42097 11.4457 8.33317 11.6667 8.33317ZM11.9 3.33317H8.1L7.8225 4.1665H12.1775L11.9 3.33317Z" fill="white"/></svg>'
};

Px.Editor.OrganizePages.PageSet = class PageSet extends Px.Editor.BaseComponent {

  template() {
    let additional_classes = '';
    if (this.state.is_being_dragged) {
      additional_classes += ' px-is-dragged';
    }
    if (this.state.can_drop_before) {
      additional_classes += 'px-drop-before';
    }
    if (this.state.can_drop_after) {
      additional_classes += 'px-drop-after';
    }

    return Px.template`
      <div class="px-page-set ${additional_classes}"
           data-set-id="${this.data.set.id}"
           data-selectable="${this.isSelectable}"
           data-selected="${this.data.selected}"
           data-onclick="selectOrDeselectPageSet"
           data-ondblclick="onDblClick">
        ${Px.raw(PageSet.icons.tick_mark)}
        ${this.renderChild(Px.Editor.OrganizePages.PageThumbs, 'page-thumbs', this.pageThumbProps)}
        <div class="px-page-captions">
          ${Px.if(this.data.set.left_caption, () => {
            return Px.template`
              <div class="px-page-caption" title="${this.data.set.left_caption}">
                ${this.data.set.left_caption}
              </div>
            `;
          })}
          ${Px.if(this.data.set.center_caption, () => {
            return Px.template`
              <div class="px-page-caption" title="${this.data.set.center_caption}">
                ${this.data.set.center_caption}
              </div>
            `;
          })}
          ${Px.if(this.data.set.right_caption, () => {
            return Px.template`
              <div class="px-page-caption" title="${this.data.set.right_caption}">
                ${this.data.set.right_caption}
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  constructor(props) {
    super(props);

    Px.Component.DragAndDrop.DropTarget.init(this, {
      types: [Px.Editor.OrganizePages.PageThumbs],
      dragEnter: this.dragEnter.bind(this),
      dragLeave: this.dragLeave.bind(this),
      canDrop: this.canDrop.bind(this),
      onDrop: this.onDrop.bind(this)
    });
  }

  get dataProperties() {
    return {
      set: {required: true},
      set_definition: {required: true},
      selected: {std: false},
      thumbnail_size: {required: true},
      store: {required: true},
      selectOrDeselectSet: {required: true},
      onDblClick: {required: false}
    };
  }

  static get properties() {
    return {
      is_being_dragged: {std: false},
      can_drop_before: {std: false},
      can_drop_after: {std: false}
    }
  }

  static get computedProperties() {
    return {
      isSelectable: function() {
        const set = this.data.set;
        const definition = this.data.set_definition;
        // Only FULL sets from the grow set can be deleted/duplicated.
        // Partially filled sets can never be deleted/duplicated or moved around.
        return definition.grow && set.pages.length === definition.pages.length;
      },
      pageThumbProps: function() {
        return {
          set: this.data.set,
          is_selectable: this.isSelectable,
          thumbnail_size: this.data.thumbnail_size
        }
      }
    };
  }

  // ------------
  // DnD handlers
  // ------------

  dragEnter(item) {
    if (this.canDrop(item)) {
      if (item.set.position > this.data.set.position) {
        this.state.can_drop_before = true;
      } else {
        this.state.can_drop_after = true;
      }
    }
  }

  dragLeave() {
    mobx.runInAction(() => {
      this.state.can_drop_before = false;
      this.state.can_drop_after = false;
    });
  }

  canDrop(item) {
    return this.isSelectable && this.data.set.id !== item.set.id;
  }

  onDrop(item) {
    let drop_position = this.data.set.position;
    if (drop_position > item.set.position) {
      drop_position += 1;
    }
    this.data.store.moveSet(item.set, drop_position);
  }

  // --------------
  // Event handlers
  // --------------

  selectOrDeselectPageSet(evt) {
    if (this.isSelectable) {
      this.data.selectOrDeselectSet(this.data.set);
    }
  }

  onDblClick(evt) {
    if (this.data.onDblClick) {
      this.data.onDblClick(this.data.set);
    }
  }

};

Px.Editor.OrganizePages.PageSet.icons = {
  tick_mark: '<svg class="px-tick-mark" width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" stroke="#fff8" stroke-width="1" fill="currentColor" /><polyline stroke="#fff" stroke-width="2" fill="none" points="4,9 7,11 12,4" /></svg>'
};

Px.Editor.OrganizePages.PageThumbs = class PageThumbs extends Px.Editor.BaseComponent {

  template() {
    const r = this.renderChild;
    return Px.template`
      <div class="px-page-thumbs">
        ${this.data.set.pages.map(page => {
          return this.renderChild(Px.Editor.Page, `page-${page.id}`, this.pageProps(page));
        })}
        ${r(Px.Editor.ImageUploadProgressIndicator, 'upload-progress-indicator', {image: this.image})}
      </div>
    `;
  }

  constructor(props) {
    super(props);

    this.on('mount', () => {
      Px.Component.DragAndDrop.DragSource.init(this, {
        canDrag: this.canDrag.bind(this),
        beginDrag: this.beginDrag.bind(this),
        endDrag: this.endDrag.bind(this),
        revert_duration: 150,
        dragged_copy: {
          opacity: 0.6,
          class_name: 'px-dragged-copy'
        }
      });
    });
  }

  get dataProperties() {
    return {
      set: {required: true},
      is_selectable: {required: true},
      thumbnail_size: {required: true},
      store: {required: true}
    };
  }

  static get properties() {
    return {
      is_being_dragged: {std: false},
      can_drop_before: {std: false},
      can_drop_after: {std: false}
    }
  }

  static get computedProperties() {
    return {
      image: function() {
        const page = this.data.set.pages[0];
        if (page) {
          const image_element = page.image_elements.find(image_element => {
            const image = image_element.image;
            return image.type === 'local';
          });
          if (image_element) {
            return image_element.image;
          }
        }
        return null;
      }
    };
  }

  pageProps(page) {
    const height = this.data.thumbnail_size;
    const scale = page.height / height;
    const width = scale === 0 ? 0 : page.width / scale;

    return {
      page: page,
      available_width: width,
      available_height: height,
      preview_mode: true,
      render_controls: false
    };
  }

  // ------------
  // DnD Handlers
  // ------------

  canDrag() {
    return this.data.is_selectable;
  }

  beginDrag() {
    this.state.is_being_dragged = true;
    return {set: this.data.set};
  }

  endDrag() {
    this.state.is_being_dragged = false;
  }

};
